import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CRMApiService,
  CrmObject,
  CrmObjectSearch,
  ListCrmObjectsRequest,
  ListCrmObjectsResponse,
} from '@vendasta/crm';
import { CrmFieldService, CrmObjectDisplayService, StandardExternalIds } from '@galaxy/crm/static';
import { debounceTime, startWith } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatButton } from '@angular/material/button';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';

export interface DialogData {
  namespace: string;
}

export interface ContactData {
  contactID: string;
  namespace: string;
  email: string;
  name: string;
}

@Component({
  selector: 'email-contact-selector-modal',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    GalaxyFormFieldModule,
    MatDialogTitle,
    MatDialogContent,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatButton,
    MatDialogActions,
    GalaxyInfiniteScrollTriggerModule,
  ],
  templateUrl: './contact-selector-modal.component.html',
  styleUrl: './contact-selector-modal.component.scss',
})
export class ContactSelectorModalComponent {
  protected readonly contactSearchFormField = new FormControl('');
  private readonly pageSize = 20;

  protected readonly hasMore = signal(false);
  private readonly cursor = signal('');
  protected contacts = signal<ContactData[]>([]);
  protected readonly loading = signal(true);

  private readonly loadMore$$ = new BehaviorSubject<boolean>(false);
  private readonly searchTerm$ = this.contactSearchFormField.valueChanges.pipe(
    startWith(''),
    debounceTime(500),
    distinctUntilChanged(),
    tap((_) => {
      this.contacts.set([]);
      this.hasMore.set(false);
      this.cursor.set('');
    }),
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: DialogData,
    private readonly snackbarService: SnackbarService,
    private readonly crmService: CRMApiService,
    private readonly crmFieldService: CrmFieldService,
    private readonly objectDisplayService: CrmObjectDisplayService,
    private readonly dialogRef: MatDialogRef<ContactSelectorModalComponent>,
  ) {
    combineLatest([this.searchTerm$, this.loadMore$$])
      .pipe(
        takeUntilDestroyed(),
        tap((_) => this.loading.set(true)),
        switchMap(([searchTerm, _]) => {
          const request = {
            namespace: this.data.namespace,
            search: { searchTerm } as CrmObjectSearch,
            pagingOptions: { pageSize: this.pageSize, cursor: this.cursor() },
          } as ListCrmObjectsRequest;
          return this.crmService.listContacts(request).pipe(
            catchError((_) => {
              this.snackbarService.openErrorSnack('EMAIL_BUILDER.CONTACT_SELECTOR.ERROR.SEARCH_FAILED');
              return of({} as ListCrmObjectsResponse);
            }),
          );
        }),
      )
      .subscribe((response) => {
        const fetchedContacts =
          (response?.crmObjects?.map((contact: CrmObject) => ({
            name: this.getNameForContact(contact),
            email: this.getEmailForContact(contact),
            namespace: this.data.namespace,
            contactID: contact.crmObjectId,
          })) as ContactData[]) ?? [];
        this.contacts.update((prev) => prev.concat(fetchedContacts));
        this.cursor.set(response?.pagingMetadata?.nextCursor ?? '');
        this.hasMore.set(response?.pagingMetadata?.hasMore ?? false);
        this.loading.set(false);
      });
  }

  loadMoreContacts(): void {
    if (this.hasMore()) {
      this.loadMore$$.next(true);
    }
  }

  selectContact(contact: ContactData): void {
    this.dialogRef.close(contact);
  }

  getNameForContact(contact: CrmObject): string {
    return this.objectDisplayService.getObjectDisplayName('Contact', contact);
  }

  getEmailForContact(contact: CrmObject): string {
    const email = this.crmFieldService.getFieldValueFromCrmObject(contact, StandardExternalIds.Email);
    return email?.stringValue;
  }

  cancel() {
    this.dialogRef.close({});
  }
}
